import type { AnalyticsEventMetadata, AnalyticsEventRequestItem } from '@resistapp/common/api-types';
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { shouldSuppressAnalyticsOnUnload } from '../utils/auth-changing-navigation';
import { API_URL, getAuthHeaders } from '../utils/fetch';
import {
  AnalyticsLocationSnapshot,
  buildAnalyticsEvent,
  getLocationAnalyticsActionType,
  isResultReviewPath,
  normaliseAnalyticsQuery,
  shouldTrackAnalyticsKeydown,
} from './analytics-utils';
import {
  AnalyticsUploadError,
  appendPendingLocationEventToQueue,
  coalescePendingLocationEvent,
  didAnalyticsIdentityChange,
  isCurrentAnalyticsFlushState,
  isTerminalAnalyticsUploadFailure,
  PendingAnalyticsLocationEvent,
  parseStoredAnalyticsLocation,
  requeueAnalyticsEventsAfterFailure,
  serialiseAnalyticsLocation,
  shouldStartAnalyticsFlush,
} from './analytics-context-utils';
import { useUser } from './use-user-context';

type TrackDownloadParams = AnalyticsEventMetadata & {
  downloadKind: 'result' | 'graph' | 'legend';
  locationSnapshot?: AnalyticsLocationSnapshot;
  occurredAt?: string;
};

type AnalyticsContextType = {
  resetAnalyticsState: () => void;
  trackDownload: (params: TrackDownloadParams) => void;
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  resetAnalyticsState: () => {},
  trackDownload: () => {},
});

const analyticsSessionStorageKey = 'resistapp.analytics.sessionId';
const analyticsLocationSessionStorageKey = 'resistapp.analytics.location';
const interactionWindowMs = 1500;
const locationSettleMs = 350;
const flushDelayMs = 5000;
const maxBatchSize = 50;
const maxQueuedEvents = 200;

type UserInteraction = {
  id: number;
  occurredAt: number;
  source: 'pointer' | 'keyboard';
};

function createAnalyticsSessionId(): string {
  return typeof window.crypto.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function storeAnalyticsSessionId(sessionId: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(analyticsSessionStorageKey, sessionId);
  } catch {
    // Best-effort only.
  }
}

function getStoredAnalyticsLocation(): AnalyticsLocationSnapshot | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return parseStoredAnalyticsLocation(window.sessionStorage.getItem(analyticsLocationSessionStorageKey));
  } catch {
    return null;
  }
}

function storeAnalyticsLocation(location: AnalyticsLocationSnapshot): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(analyticsLocationSessionStorageKey, serialiseAnalyticsLocation(location));
  } catch {
    // Best-effort only.
  }
}

function clearStoredAnalyticsLocation(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(analyticsLocationSessionStorageKey);
  } catch {
    // Best-effort only.
  }
}

function getOrCreateAnalyticsSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const existingId = window.sessionStorage.getItem(analyticsSessionStorageKey);
    if (existingId) {
      return existingId;
    }

    const nextId = createAnalyticsSessionId();
    storeAnalyticsSessionId(nextId);
    return nextId;
  } catch {
    return createAnalyticsSessionId();
  }
}

async function postAnalyticsEvents(events: AnalyticsEventRequestItem[], keepalive: boolean): Promise<void> {
  const response = await fetch(`${API_URL}/analytics/events`, {
    method: 'POST',
    headers: getAuthHeaders('application/json'),
    body: JSON.stringify({ events }),
    credentials: 'include',
    keepalive,
  });

  if (!response.ok) {
    throw new AnalyticsUploadError(response.status);
  }
}

function buildLocationEventMetadata(
  previousLocation: AnalyticsLocationSnapshot | null,
  trigger: 'pointer' | 'keyboard' | 'history' | 'initial',
): AnalyticsEventMetadata | undefined {
  const metadata: AnalyticsEventMetadata = {
    trigger,
  };

  if (previousLocation) {
    metadata.previousPath = previousLocation.pathname;
    const previousQuery = normaliseAnalyticsQuery(previousLocation.search);
    if (previousQuery) {
      metadata.previousQuery = previousQuery;
    }
  }

  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

export function AnalyticsProvider({ children }: PropsWithChildren) {
  const { user } = useUser();
  const location = useLocation();
  const navigationType = useNavigationType();
  const currentUserIdentityKey = user === undefined ? undefined : (user?.id ?? null);

  const sessionIdRef = useRef<string | null>(getOrCreateAnalyticsSessionId());
  const queueRef = useRef<AnalyticsEventRequestItem[]>([]);
  const trackedLocationRef = useRef<AnalyticsLocationSnapshot | null>(getStoredAnalyticsLocation());
  const lastInteractionRef = useRef<UserInteraction | null>(null);
  const nextInteractionIdRef = useRef(0);
  const pendingLocationEventRef = useRef<PendingAnalyticsLocationEvent | null>(null);
  const pendingLocationTimeoutRef = useRef<number | null>(null);
  const flushTimeoutRef = useRef<number | null>(null);
  const activeFlushCountRef = useRef(0);
  const stateVersionRef = useRef(0);
  const userIdentityKeyRef = useRef<number | null | undefined>(currentUserIdentityKey);
  const analyticsDisabledRef = useRef(false);
  const flushQueueRef = useRef<(keepalive?: boolean) => Promise<void>>(async () => {});

  const clearPendingLocationTimeoutStable = useCallback(() => {
    if (pendingLocationTimeoutRef.current == null) {
      return;
    }
    window.clearTimeout(pendingLocationTimeoutRef.current);
    pendingLocationTimeoutRef.current = null;
  }, []);

  const clearFlushTimeoutStable = useCallback(() => {
    if (flushTimeoutRef.current == null) {
      return;
    }
    window.clearTimeout(flushTimeoutRef.current);
    flushTimeoutRef.current = null;
  }, []);

  const scheduleFlushStable = useCallback(
    (delayMs = flushDelayMs) => {
      clearFlushTimeoutStable();
      flushTimeoutRef.current = window.setTimeout(() => {
        void flushQueueRef.current();
      }, delayMs);
    },
    [clearFlushTimeoutStable],
  );

  const flushQueueStable = useCallback(
    async (keepalive = false) => {
      if (keepalive && pendingLocationEventRef.current) {
        queueRef.current = appendPendingLocationEventToQueue({
          currentQueuedEvents: queueRef.current,
          maxQueuedEvents,
          pendingLocationEvent: pendingLocationEventRef.current,
        });
        pendingLocationEventRef.current = null;
        clearPendingLocationTimeoutStable();
      }

      const sessionId = sessionIdRef.current;
      if (
        !shouldStartAnalyticsFlush({
          activeFlushCount: activeFlushCountRef.current,
          analyticsDisabled: analyticsDisabledRef.current,
          hasQueuedEvents: queueRef.current.length > 0,
          keepalive,
          sessionId,
        })
      ) {
        return;
      }

      clearFlushTimeoutStable();
      const events = queueRef.current.splice(0, maxBatchSize);
      const stateVersion = stateVersionRef.current;
      activeFlushCountRef.current += 1;

      try {
        await postAnalyticsEvents(events, keepalive);
      } catch (error) {
        if (error instanceof AnalyticsUploadError && isTerminalAnalyticsUploadFailure(error.status)) {
          if (isCurrentAnalyticsFlushState(stateVersionRef.current, stateVersion)) {
            analyticsDisabledRef.current = true;
            queueRef.current = [];
            pendingLocationEventRef.current = null;
            clearPendingLocationTimeoutStable();
            clearFlushTimeoutStable();
            console.warn('Dropping analytics events after auth failure', error);
          }
          return;
        }

        if (isCurrentAnalyticsFlushState(stateVersionRef.current, stateVersion)) {
          queueRef.current = requeueAnalyticsEventsAfterFailure({
            currentQueuedEvents: queueRef.current,
            currentStateVersion: stateVersionRef.current,
            failedEvents: events,
            flushStateVersion: stateVersion,
            maxQueuedEvents,
          });
          console.warn('Failed to upload analytics events', error);
        }
      } finally {
        const isCurrentStateVersion = isCurrentAnalyticsFlushState(stateVersionRef.current, stateVersion);
        if (isCurrentStateVersion) {
          activeFlushCountRef.current = Math.max(activeFlushCountRef.current - 1, 0);
          if (queueRef.current.length > 0 && !analyticsDisabledRef.current && activeFlushCountRef.current === 0) {
            scheduleFlushStable();
          }
        }
      }
    },
    [clearFlushTimeoutStable, clearPendingLocationTimeoutStable, scheduleFlushStable],
  );
  flushQueueRef.current = flushQueueStable;

  const resetAnalyticsStateStable = useCallback(() => {
    stateVersionRef.current += 1;
    analyticsDisabledRef.current = false;
    queueRef.current = [];
    trackedLocationRef.current = null;
    lastInteractionRef.current = null;
    nextInteractionIdRef.current = 0;
    pendingLocationEventRef.current = null;
    activeFlushCountRef.current = 0;
    clearPendingLocationTimeoutStable();
    clearFlushTimeoutStable();
    clearStoredAnalyticsLocation();

    const nextSessionId = createAnalyticsSessionId();
    sessionIdRef.current = nextSessionId;
    storeAnalyticsSessionId(nextSessionId);
  }, [clearFlushTimeoutStable, clearPendingLocationTimeoutStable]);

  const enqueueEventStable = useCallback(
    (event: AnalyticsEventRequestItem) => {
      if (analyticsDisabledRef.current) {
        return;
      }

      queueRef.current.push(event);
      if (queueRef.current.length > maxQueuedEvents) {
        queueRef.current = queueRef.current.slice(-maxQueuedEvents);
      }

      if (queueRef.current.length >= maxBatchSize) {
        void flushQueueStable();
        return;
      }

      scheduleFlushStable();
    },
    [flushQueueStable, scheduleFlushStable],
  );

  const trackDownload = useCallback(
    (params: TrackDownloadParams) => {
      const sessionId = sessionIdRef.current;
      const { locationSnapshot, occurredAt, ...metadata } = params;
      const trackedLocation = locationSnapshot ?? { pathname: location.pathname, search: location.search };
      if (!user || !sessionId || !isResultReviewPath(trackedLocation.pathname)) {
        return;
      }

      enqueueEventStable(
        buildAnalyticsEvent({
          actionType: 'download',
          location: trackedLocation,
          occurredAt,
          sessionId,
          metadata,
        }),
      );
    },
    [enqueueEventStable, location.pathname, location.search, user?.id],
  );

  useEffect(() => {
    const handlePointerDown = () => {
      nextInteractionIdRef.current += 1;
      lastInteractionRef.current = {
        id: nextInteractionIdRef.current,
        occurredAt: Date.now(),
        source: 'pointer',
      };
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!shouldTrackAnalyticsKeydown(event)) {
        return;
      }

      nextInteractionIdRef.current += 1;
      lastInteractionRef.current = {
        id: nextInteractionIdRef.current,
        occurredAt: Date.now(),
        source: 'keyboard',
      };
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    const nextIdentityKey = currentUserIdentityKey;
    const previousIdentityKey = userIdentityKeyRef.current;
    userIdentityKeyRef.current = nextIdentityKey;

    if (!didAnalyticsIdentityChange(previousIdentityKey, nextIdentityKey)) {
      return;
    }

    resetAnalyticsStateStable();
  }, [currentUserIdentityKey, resetAnalyticsStateStable]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (shouldSuppressAnalyticsOnUnload()) {
          resetAnalyticsStateStable();
          return;
        }
        void flushQueueStable(true);
      }
    };

    const handleBeforeUnload = () => {
      if (shouldSuppressAnalyticsOnUnload()) {
        resetAnalyticsStateStable();
        return;
      }
      void flushQueueStable(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearPendingLocationTimeoutStable();
      clearFlushTimeoutStable();
    };
  }, [clearFlushTimeoutStable, clearPendingLocationTimeoutStable, flushQueueStable, resetAnalyticsStateStable]);

  useEffect(() => {
    const sessionId = sessionIdRef.current;
    const nextLocation: AnalyticsLocationSnapshot = {
      pathname: location.pathname,
      search: location.search,
    };
    const lastInteraction = lastInteractionRef.current;
    const hasRecentInteraction =
      !!lastInteraction && Date.now() - lastInteraction.occurredAt <= interactionWindowMs;
    const settleKey = lastInteraction?.id ?? 0;
    const previousPendingLocationEvent = pendingLocationEventRef.current;
    clearPendingLocationTimeoutStable();
    pendingLocationEventRef.current = null;

    if (
      previousPendingLocationEvent?.event.actionType === 'landing' &&
      previousPendingLocationEvent.interactionId !== settleKey
    ) {
      enqueueEventStable(previousPendingLocationEvent.event);
    }

    if (!user || !sessionId) {
      return;
    }

    const previousLocation = trackedLocationRef.current;
    trackedLocationRef.current = nextLocation;
    storeAnalyticsLocation(nextLocation);

    const actionType = getLocationAnalyticsActionType(previousLocation, nextLocation);
    if (!actionType) {
      return;
    }

    if (actionType !== 'landing' && navigationType === NavigationType.Pop) {
      enqueueEventStable(
        buildAnalyticsEvent({
          actionType,
          location: nextLocation,
          sessionId,
          metadata: buildLocationEventMetadata(previousLocation, 'history'),
        }),
      );
      return;
    }

    const isMountTimeLandingRewrite =
      actionType === 'query' &&
      previousPendingLocationEvent?.event.actionType === 'landing' &&
      previousPendingLocationEvent.interactionId === settleKey;
    const shouldTrackSettledLocationEvent =
      actionType === 'landing' || hasRecentInteraction || isMountTimeLandingRewrite;
    if (!shouldTrackSettledLocationEvent) {
      return;
    }

    pendingLocationEventRef.current = coalescePendingLocationEvent(previousPendingLocationEvent, {
      interactionId: settleKey,
      event: buildAnalyticsEvent({
        actionType,
        location: nextLocation,
        sessionId,
        metadata: buildLocationEventMetadata(previousLocation, hasRecentInteraction ? lastInteraction.source : 'initial'),
      }),
    });

    clearPendingLocationTimeoutStable();
    pendingLocationTimeoutRef.current = window.setTimeout(() => {
      const pendingEvent = pendingLocationEventRef.current;
      if (!pendingEvent || pendingEvent.interactionId !== settleKey) {
        return;
      }

      pendingLocationEventRef.current = null;
      enqueueEventStable(pendingEvent.event);
    }, locationSettleMs);
  }, [
    clearPendingLocationTimeoutStable,
    enqueueEventStable,
    location.pathname,
    location.search,
    navigationType,
    user?.id,
  ]);

  return (
    <AnalyticsContext.Provider value={{ resetAnalyticsState: resetAnalyticsStateStable, trackDownload }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}
